import React, { useState, useEffect, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import useApi from "hooks/useApi";
import useApp from "hooks/useApp";

export default function ConsultaPedidosModal({ visible, onHide, funcionario }) {
    const api = useApi();
    const app = useApp();

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // 1 a 12
    const defaultYear = currentYear >= 2024 && currentYear <= 2030 ? currentYear : 2026;

    // Intervalo de anos
    const anos = useMemo(() => [
        { label: "2024", value: 2024 },
        { label: "2025", value: 2025 },
        { label: "2026", value: 2026 },
        { label: "2027", value: 2027 },
        { label: "2028", value: 2028 },
        { label: "2029", value: 2029 },
        { label: "2030", value: 2030 }
    ], []);

    // Meses de Janeiro a Dezembro (valores numéricos de 1 a 12)
    const meses = useMemo(() => [
        { label: "01 - Janeiro", value: 1 },
        { label: "02 - Fevereiro", value: 2 },
        { label: "03 - Março", value: 3 },
        { label: "04 - Abril", value: 4 },
        { label: "05 - Maio", value: 5 },
        { label: "06 - Junho", value: 6 },
        { label: "07 - Julho", value: 7 },
        { label: "08 - Agosto", value: 8 },
        { label: "09 - Setembro", value: 9 },
        { label: "10 - Outubro", value: 10 },
        { label: "11 - Novembro", value: 11 },
        { label: "12 - Dezembro", value: 12 }
    ], []);

    const [ano, setAno] = useState(defaultYear);
    const [startMonth, setStartMonth] = useState(currentMonth);
    const [endMonth, setEndMonth] = useState(12);

    const [loading, setLoading] = useState(false);
    const [pedidos, setPedidos] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);

    const funcionarioId = funcionario?.id || funcionario?.funcid || funcionario?.ID;

    // Handlers para alinhamento inteligente dos meses
    const handleStartMonthChange = (newStart) => {
        setStartMonth(newStart);
        if (newStart > endMonth) {
            setEndMonth(newStart);
        }
    };

    const handleEndMonthChange = (newEnd) => {
        setEndMonth(newEnd);
        if (newEnd < startMonth) {
            setStartMonth(newEnd);
        }
    };

    const fetchPedidos = async (selectedAno, selectedStartMonth, selectedEndMonth) => {
        if (!funcionarioId) return;

        setLoading(true);
        setHasSearched(true);

        try {
            const payload = {
                funcID: Number(funcionarioId),
                year: Number(selectedAno),
                startMonth: Number(selectedStartMonth),
                endMonth: Number(selectedEndMonth)
            };

            const response = await api.post("Pedidos/pedidos/funcionario/resume", payload);

            let data = [];
            if (Array.isArray(response?.data)) {
                data = response.data;
            } else if (response?.data?.data && Array.isArray(response.data.data)) {
                data = response.data.data;
            }

            setPedidos(data);
        } catch (error) {
            console.error("Erro ao consultar resumo de pedidos do funcionário:", error);
            if (app?.flashMsg) {
                app.flashMsg("Consulta de Pedidos", "Erro ao carregar dados do funcionário.", "error");
            }
            setPedidos([]);
        } finally {
            setLoading(false);
        }
    };

    // Efeito disparado ESTRITAMENTE quando o modal abre ou altera o ID do funcionário
    useEffect(() => {
        if (visible && funcionarioId) {
            setAno(defaultYear);
            setStartMonth(currentMonth);
            setEndMonth(12);
            setPedidos([]);
            setHasSearched(false);
            fetchPedidos(defaultYear, currentMonth, 12);
        } else if (!visible) {
            setPedidos([]);
            setHasSearched(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, funcionarioId]);

    const handleSearch = () => {
        if (startMonth > endMonth) {
            if (app?.flashMsg) {
                app.flashMsg("Atenção", "O mês inicial não pode ser maior que o mês final.", "warn");
            }
            return;
        }
        fetchPedidos(ano, startMonth, endMonth);
    };

    const handleReset = () => {
        setAno(defaultYear);
        setStartMonth(currentMonth);
        setEndMonth(12);
        fetchPedidos(defaultYear, currentMonth, 12);
    };

    const formatCurrency = (value) => {
        const num = Number(value) || 0;
        return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        try {
            const raw = String(dateStr).split("T")[0];
            const parts = raw.split("-");
            if (parts.length === 3) {
                const [y, m, d] = parts;
                return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
            }
            return dateStr;
        } catch {
            return dateStr;
        }
    };

    const totalItens = pedidos.reduce((acc, curr) => acc + (Number(curr.qtde) || 0), 0);
    const valorTotalGeral = pedidos.reduce((acc, curr) => {
        const tot = curr.total !== undefined ? Number(curr.total) : (Number(curr.qtde) || 0) * (Number(curr.unit) || 0);
        return acc + (tot || 0);
    }, 0);

    const getInitials = (name) => {
        if (!name) return "FN";
        const parts = name.trim().split(" ");
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    const dialogFooter = (
        <div className="flex justify-content-between align-items-center w-full">
            <div className="text-sm text-500">
                {pedidos.length > 0 && (
                    <span>Exibindo <b>{pedidos.length}</b> linha(s) encontrada(s)</span>
                )}
            </div>
            <div className="flex gap-2">
                <Button
                    label="Fechar"
                    icon="pi pi-times"
                    className="p-button-outlined p-button-secondary"
                    onClick={onHide}
                />
            </div>
        </div>
    );

    return (
        <Dialog
            header="Consulta de Pedidos e Produtos do Funcionário"
            visible={visible}
            style={{ width: "88vw", maxWidth: "1200px" }}
            modal
            className="consulta-dialog"
            onHide={onHide}
            footer={dialogFooter}
        >
            {/* Bloco 1: Contexto e Dados do Funcionário */}
            {funcionario && (
                <div className="employee-context-box flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-3">
                    <div className="flex align-items-center gap-3">
                        <div className="employee-avatar shadow-1">
                            {getInitials(funcionario.nome)}
                        </div>
                        <div>
                            <div className="text-xl font-bold text-900 line-height-2">
                                {funcionario.nome || "Não informado"}
                            </div>
                            <div className="flex flex-wrap align-items-center gap-2 mt-1">
                                <Tag severity="info" value={`ID: ${funcionarioId || "-"}`} className="font-mono text-xs" />
                                <Tag severity="info" value={`Matrícula: ${funcionario.matricula || "-"}`} className="font-mono text-xs" />
                                <Tag severity="warning" value={`CPF: ${funcionario.cpf || "-"}`} className="font-mono text-xs" />
                                {funcionario.apelido && (
                                    <span className="text-xs text-500 font-italic">
                                        ({funcionario.apelido})
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div>
                        <Tag
                            severity={funcionario.active === 1 || funcionario.active === "1" ? "success" : "danger"}
                            value={funcionario.active === 1 || funcionario.active === "1" ? "Ativo" : "Bloqueado"}
                            icon={funcionario.active === 1 || funcionario.active === "1" ? "pi pi-check" : "pi pi-lock"}
                        />
                    </div>
                </div>
            )}

            {/* Bloco 2: Filtros de Período (Ano, Mês Inicial e Mês Final) */}
            <div className="consulta-filter-panel shadow-1">
                <div className="text-sm font-semibold text-700 mb-2 flex align-items-center gap-2">
                    <i className="pi pi-calendar text-primary" />
                    <span>Filtros por Período de Apuração</span>
                </div>
                <div className="grid p-fluid align-items-end">
                    <div className="col-12 sm:col-6 md:col-3">
                        <label className="block text-xs font-medium text-600 mb-1">Ano</label>
                        <Dropdown
                            value={ano}
                            options={anos}
                            onChange={(e) => setAno(e.value)}
                            placeholder="Selecione o ano"
                            className="p-inputtext-sm w-full"
                        />
                    </div>
                    <div className="col-12 sm:col-6 md:col-3">
                        <label className="block text-xs font-medium text-600 mb-1">Mês Inicial</label>
                        <Dropdown
                            value={startMonth}
                            options={meses}
                            onChange={(e) => handleStartMonthChange(e.value)}
                            placeholder="Mês inicial"
                            className="p-inputtext-sm w-full"
                        />
                    </div>
                    <div className="col-12 sm:col-6 md:col-3">
                        <label className="block text-xs font-medium text-600 mb-1">Mês Final</label>
                        <Dropdown
                            value={endMonth}
                            options={meses}
                            onChange={(e) => handleEndMonthChange(e.value)}
                            placeholder="Mês final"
                            className="p-inputtext-sm w-full"
                        />
                    </div>
                    <div className="col-12 md:col-3 flex gap-2">
                        <Button
                            type="button"
                            label="Filtrar"
                            icon="pi pi-search"
                            className="p-button-primary p-button-sm flex-1"
                            onClick={handleSearch}
                            loading={loading}
                        />
                        <Button
                            type="button"
                            icon="pi pi-replay"
                            tooltip="Limpar e Recarregar"
                            tooltipOptions={{ position: "top" }}
                            className="p-button-outlined p-button-secondary p-button-sm"
                            onClick={handleReset}
                            disabled={loading}
                        />
                    </div>
                </div>
            </div>

            {/* Bloco 3: Tabela de Pedidos e Produtos */}
            <div className="mt-2">
                {loading ? (
                    <div className="flex flex-column align-items-center justify-content-center p-5 text-gray-500">
                        <ProgressSpinner style={{ width: "45px", height: "45px" }} />
                        <span className="mt-2 text-sm font-semibold">Consultando pedidos do colaborador...</span>
                    </div>
                ) : (
                    <>
                        <DataTable
                            value={pedidos}
                            className="p-datatable-sm shadow-1"
                            stripedRows
                            responsiveLayout="stack"
                            rowHover
                            paginator
                            rows={5}
                            rowsPerPageOptions={[5]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} pedidos"
                            emptyMessage={
                                <div className="text-center p-4 text-500">
                                    <i className="pi pi-inbox text-3xl mb-2 block text-primary" style={{ opacity: 0.6 }} />
                                    <p className="font-semibold mb-1">
                                        {hasSearched 
                                            ? "Nenhum pedido encontrado para os filtros selecionados." 
                                            : "Selecione o período e clique em Filtrar."}
                                    </p>
                                    <span className="text-xs text-400">Verifique o funcionário, ano e intervalo de meses informados.</span>
                                </div>
                            }
                        >
                            <Column
                                field="pedido"
                                header="Pedido"
                                sortable
                                align="center"
                                headerStyle={{ width: "10%" }}
                                className="font-semibold"
                            />
                            <Column
                                field="emissao"
                                header="Emissão"
                                sortable
                                align="center"
                                headerStyle={{ width: "12%" }}
                                body={(rowData) => formatDate(rowData.emissao)}
                            />
                            <Column
                                field="codProduto"
                                header="Cód. Produto"
                                sortable
                                align="center"
                                className="font-mono text-sm"
                                headerStyle={{ width: "13%" }}
                            />
                            <Column
                                field="produto"
                                header="Produto"
                                sortable
                                align="left"
                                headerStyle={{ width: "35%" }}
                            />
                            <Column
                                field="qtde"
                                header="Qtde"
                                sortable
                                align="right"
                                headerStyle={{ width: "8%" }}
                            />
                            <Column
                                field="unit"
                                header="Unitário"
                                sortable
                                align="right"
                                headerStyle={{ width: "11%" }}
                                body={(rowData) => formatCurrency(rowData.unit)}
                            />
                            <Column
                                field="total"
                                header="Total"
                                sortable
                                align="right"
                                headerStyle={{ width: "11%" }}
                                body={(rowData) => (
                                    <span className="font-bold text-900">
                                        {formatCurrency(rowData.total !== undefined ? rowData.total : (Number(rowData.qtde) || 0) * (Number(rowData.unit) || 0))}
                                    </span>
                                )}
                            />
                        </DataTable>

                        {/* Cards de Resumo */}
                        {pedidos.length > 0 && (
                            <div className="grid mt-3">
                                <div className="col-12 sm:col-4">
                                    <div className="consulta-summary-card flex align-items-center justify-content-between">
                                        <div>
                                            <div className="text-xs text-500 font-medium">Total de Linhas</div>
                                            <div className="text-xl font-bold text-900">{pedidos.length}</div>
                                        </div>
                                        <i className="pi pi-list text-2xl text-blue-500" />
                                    </div>
                                </div>
                                <div className="col-12 sm:col-4">
                                    <div className="consulta-summary-card flex align-items-center justify-content-between">
                                        <div>
                                            <div className="text-xs text-500 font-medium">Quantidade Total</div>
                                            <div className="text-xl font-bold text-900">{totalItens}</div>
                                        </div>
                                        <i className="pi pi-box text-2xl text-orange-500" />
                                    </div>
                                </div>
                                <div className="col-12 sm:col-4">
                                    <div className="consulta-summary-card flex align-items-center justify-content-between">
                                        <div>
                                            <div className="text-xs text-500 font-medium">Valor Total Acumulado</div>
                                            <div className="text-xl font-bold text-green-600">{formatCurrency(valorTotalGeral)}</div>
                                        </div>
                                        <i className="pi pi-dollar text-2xl text-green-500" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Dialog>
    );
}
